import type React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {Alert, Box, InputAdornment, Link, MenuItem, Stack, TextField, Typography} from '@mui/material';
import {Search} from '@mui/icons-material';
import type {GridColDef, GridPaginationModel, GridSortModel} from '@mui/x-data-grid-premium';
import {generatePath, Link as RouterLink, useOutletContext} from 'react-router';
import ReactECharts from 'echarts-for-react';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import {formatDate} from '~/components/data-grid/cells/gridCellFormat';
import {SegmentedControl} from '~/components/segmented-control/SegmentedControl';
import {routes} from '~/routing/routes';
import {SupplierCountryFlag} from '~/pages/supplier-360/components/SupplierCountryFlag';
import {SupplierNameAvatar} from '~/pages/supplier-360/components/SupplierNameAvatar';
import {toSupplierOrdering} from '../../domain/profileTables';
import {useProfileSuppliersQuery} from '../../hooks/useCategoryProfileQueries';
import type {SupplierRow} from '../../types/categoryProfileTypes';
import {ProfileSection} from '../components/ProfileSection';
import type {CategoryProfileOutletContext} from '../profileTypes';

const CUTS = [{label: 'All spend', pct: 100}, {label: 'Top 80%', pct: 80}, {label: 'Top 50%', pct: 50}, {label: 'Top 20%', pct: 20}];
const ALL = '';
const VIEWS = [{label: 'By supplier', group: 'supplier'}, {label: 'By normalized name', group: 'normalized'}] as const;

/**
 * Suppliers tab (spec 4.6): the supplier Pareto, then the supplier list with spend cuts, by supplier or by normalized
 * name (Supplier 360 entity, else supplier normalization). Names link to the Supplier 360 detail page where it has one. The list is paged, sorted, searched
 * and filtered on the server, so a category with tens of thousands of suppliers loads one page at a time.
 */
export const SuppliersTab: React.FC = () => {
    const {databagId, path, header, money} = useOutletContext<CategoryProfileOutletContext>();
    const [cut, setCut] = useState(0);
    const [pagination, setPagination] = useState<GridPaginationModel>({page: 0, pageSize: 25});
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'spend', sort: 'desc'}]);
    const [searchText, setSearchText] = useState('');
    const [search, setSearch] = useState('');
    const [country, setCountry] = useState(ALL);
    const [view, setView] = useState(0);

    // Search as you type, without a request per key press.
    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchText.trim());
            setPagination(p => ({...p, page: 0}));
        }, 300);
        return () => clearTimeout(t);
    }, [searchText]);
    // A new filter starts at the first page.
    const firstPage = () => setPagination(p => ({...p, page: 0}));

    const {data, isFetching, isError} = useProfileSuppliersQuery(databagId, {
        path, ordering: toSupplierOrdering(sortModel), page: pagination.page, pageSize: pagination.pageSize,
        search, country, cut: CUTS[cut].pct, group: VIEWS[view].group,
    });
    const grouped = VIEWS[view].group === 'normalized';
    const detailLink = (entityId: number) =>
        `${generatePath(routes.supplier_360, {id: String(databagId)})}/${entityId}/overview`;

    const columns: GridColDef<SupplierRow>[] = useMemo(() => [
        {field: 'name', headerName: grouped ? 'Normalized supplier' : 'Supplier', flex: 1, minWidth: 240,
            valueGetter: (_v, r) => r.name || r.supplierCode,
            renderCell: ({row, value}) => {
                // Second line: the normalized name of a supplier, or how many suppliers a normalized row sums.
                const note = grouped ? (row.members > 1 ? `${row.members} suppliers` : null)
                    : (row.normalizedName && row.normalizedName !== row.name ? row.normalizedName : null);
                const text = (
                    <Stack justifyContent="center" sx={{height: '100%', minWidth: 0, lineHeight: 1.3}}>
                        {row.entityId ? (
                            <Link component={RouterLink} to={detailLink(row.entityId)} underline="hover" noWrap
                                  title="Open in Supplier 360">{value}</Link>
                        ) : <Typography variant="body2" noWrap>{value}</Typography>}
                        {note && <Typography variant="caption" color="text.secondary" noWrap>{note}</Typography>}
                    </Stack>
                );
                // Normalized rows from Supplier 360 show its company logo (initials without one), as Supplier 360 does.
                if (!grouped || !row.entityId) return text;
                return (
                    <Stack direction="row" alignItems="center" gap={1} sx={{height: '100%', minWidth: 0}}>
                        <SupplierNameAvatar name={String(value)} entityId={row.entityId} logoUrl={row.hasLogo ? 'logo' : ''} size={24}/>
                        {text}
                    </Stack>
                );
            }},
        {field: 'country', headerName: 'Origin country', width: 130, sortable: false,
            renderCell: ({row, value}) => grouped && row.entityCountry ? (
                <Stack direction="row" alignItems="center" gap={0.75} sx={{height: '100%'}}>
                    <SupplierCountryFlag country={{id: 0, name: row.entityCountry.name, code_a2: row.entityCountry.code,
                        code_a3: '', flag: ''}}/>
                    <span>{row.entityCountry.code}</span>
                </Stack>
            ) : value},
        {field: 'spend', headerName: 'Total spend', type: 'number', width: 130, valueFormatter: (v: number) => money(v)},
        {field: 'spendSharePct', headerName: 'Spend %', type: 'number', width: 100,
            valueFormatter: (v: number | null) => v === null ? '—' : `${v.toFixed(1)}%`},
        ...(header.kpis.showSpendUnderManagement
            ? [{field: 'poSpend', headerName: 'PO spend', type: 'number', width: 130, valueFormatter: (v: number) => money(v)} as GridColDef<SupplierRow>]
            : []),
        {field: 'transactions', headerName: 'Transactions', type: 'number', width: 120},
        {field: 'lastMonth', headerName: 'Last spend', width: 130, valueFormatter: (v: string | null) => formatDate(v)},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [money, grouped, databagId, header.kpis.showSpendUnderManagement]);

    if (isError) return <Alert severity="error">Could not load the suppliers.</Alert>;

    const pareto = data?.pareto;
    const filtered = Boolean(search || country || CUTS[cut].pct < 100);
    return (
        <Stack gap={2}>
            {pareto && pareto.suppliers > 0 && (
                <ProfileSection title={`Supplier Pareto: ${pareto.carrying80.toLocaleString()} of ${pareto.suppliers.toLocaleString()} suppliers carry 80% of spend`}>
                    {(pareto.topSharePct ?? 0) >= 60 && (
                        <Typography variant="body2" color="error.main" sx={{mb: 1}}>
                            {`Single-source exposure: the top supplier holds ${pareto.topSharePct?.toFixed(0)}% of spend.`}
                        </Typography>
                    )}
                    <ReactECharts style={{height: 240}} notMerge option={{
                        grid: {left: 48, right: 16, top: 16, bottom: 32},
                        tooltip: {trigger: 'axis', formatter: (ps: {value: [number, number]}[]) =>
                            `${ps[0].value[0].toLocaleString()} suppliers: ${ps[0].value[1]}% of spend`},
                        xAxis: {type: 'value', name: 'Suppliers', nameLocation: 'middle', nameGap: 20, min: 1, max: pareto.suppliers,
                            axisLabel: {formatter: (v: number) => v.toLocaleString()}},
                        yAxis: {type: 'value', max: 100, axisLabel: {formatter: '{value}%'}},
                        series: [{type: 'line', showSymbol: false, data: pareto.curve.map(p => [p.rank, p.pct]),
                            areaStyle: {opacity: 0.15}, itemStyle: {color: '#2D6FF5'},
                            markLine: {symbol: 'none', data: [{yAxis: 80}], lineStyle: {type: 'dashed', color: '#7A7F8A'}}}],
                    }}/>
                </ProfileSection>
            )}
            <ProfileSection title={`Suppliers in ${header.name}`}>
                <Stack gap={1.5}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                        <SegmentedControl labels={VIEWS.map(v => v.label)} value={view} onChange={i => {
                    setView(i);
                    firstPage();
                }}/>
                        <SegmentedControl labels={CUTS.map(c => c.label)} value={cut} onChange={i => {
                    setCut(i);
                    firstPage();
                }}/>
                        <TextField size="small" placeholder="Search name, normalized name or code" value={searchText}
                                   onChange={e => setSearchText(e.target.value)} sx={{minWidth: 260, flex: {xs: 1, md: 'none'}}}
                                   slotProps={{htmlInput: {maxLength: 100, 'aria-label': 'Search suppliers'},
                                       input: {startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}}}/>
                        <TextField select size="small" label="Origin country" value={country} onChange={e => {
                               setCountry(e.target.value);
                               firstPage();
                           }}
                                   sx={{minWidth: 160}}>
                            <MenuItem value={ALL}>All countries</MenuItem>
                            {(data?.countries ?? []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                    </Stack>
                    {data && !data.normalized && (
                        <Typography variant="caption" color="text.secondary">
                            No normalized supplier names for this dataset yet (Supplier 360 or supplier normalization).
                        </Typography>
                    )}
                    <Box sx={{height: 560}}>
                        <CustomDataGrid
                    layoutKey="category-profile-suppliers"
                    rows={data?.rows ?? []} columns={columns} loading={isFetching}
                    getRowId={r => String(r.supplierId ?? r.supplierCode)}
                    rowCount={data?.count ?? 0}
                    paginationMode="server" sortingMode="server" filterMode="server"
                    paginationModel={pagination} onPaginationModelChange={setPagination}
                    sortModel={sortModel} onSortModelChange={m => {
                        setSortModel(m);
                        setPagination(p => ({...p, page: 0}));
                    }}
                    pageSizeOptions={[25, 50, 100, 200]}
                    disableRowSelectionOnClick showToolbar
                    toolbarProps={{
                        showFilterPanel: false,
                        children: (
                            <Typography variant="body2" sx={{pl: 0.5}}>
                                {data && pareto && filtered
                                    ? `${data.count.toLocaleString()} of ${pareto.suppliers.toLocaleString()} suppliers`
                                    : `${(pareto?.suppliers ?? 0).toLocaleString()} suppliers`}
                            </Typography>
                        ),
                    }}
                    localeText={{noRowsLabel: filtered ? 'No suppliers match.' : 'No suppliers with spend in this category.'}}
                        />
                    </Box>
                </Stack>
            </ProfileSection>
        </Stack>
    );
};
